/**
 * SegmentedControl — canonical design-system segmented control (src/ui/ds).
 *
 * Ported from the handoff (_c-segmented-control.*). Controlled.
 * Selected thumb = bg.component + seg-thumb shadow; track = segmented.bg.
 * `rounded` switches track + thumb + options to the pill treatment (radius full).
 * Theme-token driven.
 *
 * The thumb SLIDES between options (transitions.dev tabs-sliding: 200ms on a
 * gentle ease-out; same React port as PageTabBar): a single absolutely-
 * positioned thumb is measured onto the selected option, so switching glides
 * instead of teleporting. First paint snaps (no draw-in from origin), and a
 * prefers-reduced-motion guard stills it.
 *
 * Genuinely interactive: clicking an option fires onChange (selecting it), and
 * real :hover lifts a non-selected option's ink. `forceState` (used by the
 * gallery) pins the visual state of the *selected* option for inspection.
 *
 * Organism: composes <SegmentButton/> (with `showThumb` off — the control owns
 * the single sliding thumb, the same split PageTabBar/PageTab use for the
 * selected underline).
 *
 *
 * PORTED VERBATIM from ploy-client src/ui/ds/SegmentedControl.tsx — the canonical
 * design-system component. Do not restyle here; upstream owns it. Portal-local
 * changes only: dsTag inlined, viewer-only `whenToUse` export dropped.
 * The `motion.slide` preset is inlined as its literal value (the portal has no
 * motion-token module yet — flagged as a token gap).
 *
 * Location: src/components/ds/SegmentedControl.tsx
 */
import { Box, HStack, Tooltip } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CounterTone } from "./Counter";
import { SegmentButton } from "./SegmentButton";
import type { SegmentButtonSize } from "./SegmentButton";

/* dev-only DOM marker (inlined from ploy-client's ds/dsTag) — compiles away
   in production builds. */
const dsTag = (name: string): Record<string, string> =>
  import.meta.env.DEV ? { "data-ds": name } : {};

export interface SegmentedOption {
  label: string;
  value: string;
  /** optional leading glyph; sized by the option's font size (pass an 1em icon) */
  icon?: ReactNode;
  /** render only the icon — `label` becomes the aria-label */
  iconOnly?: boolean;
  /** hover tooltip for this option */
  tooltip?: ReactNode;
  /** trailing Counter count for this option; string for "3/13" progress */
  count?: number | string;
  /** trailing Counter tone — negative for error counts etc. */
  countTone?: CounterTone;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onChange?: (value: string) => void;
  /** sm = compact rows (filter/condition builders); md = default */
  size?: SegmentButtonSize;
  /** pill treatment: radius full on the track, thumb and options */
  rounded?: boolean;
  /** stretch the track to its container's width, options sharing it equally */
  fullWidth?: boolean;
  /** gallery only: forces the visual state of the selected option */
  forceState?: "default" | "hover" | "disabled";
}

// transitions.dev tabs-sliding tuning — the DS slide preset (upstream
// `motion.slide.css`), inlined at its literal value: the portal has no motion
// token module yet. TOKEN GAP — swap for the token when one lands.
const SLIDE_CSS = "200ms cubic-bezier(0.22, 1, 0.36, 1)";
const SLIDE = `transform ${SLIDE_CSS}, width ${SLIDE_CSS}`;

export function SegmentedControl({
  options,
  value,
  onChange,
  size = "md",
  rounded = false,
  fullWidth = false,
  forceState = "default",
}: SegmentedControlProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, width: 0 });
  // first measured position snaps (transition off) so the thumb doesn't draw
  // in from origin; a post-paint effect then arms the slide for later changes
  const [armed, setArmed] = useState(false);

  const rawIndex = options.findIndex((o) => o.value === value);
  const disabled = forceState === "disabled";
  const showThumb = rawIndex >= 0 && !disabled;
  const indexRef = useRef(rawIndex);
  indexRef.current = rawIndex;

  const measure = (idx: number) => {
    const bar = barRef.current;
    if (!bar || idx < 0) return;
    const btn = bar.querySelectorAll<HTMLButtonElement>("button")[idx];
    if (!btn) return;
    const left = btn.offsetLeft;
    const width = btn.offsetWidth;
    setPos((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
  };

  useLayoutEffect(() => {
    measure(rawIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawIndex, options, value]);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure(indexRef.current));
    ro.observe(bar);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setArmed(true);
  }, []);

  return (
    <HStack
      {...dsTag("SegmentedControl")}
      ref={barRef}
      position="relative"
      display={fullWidth ? "flex" : "inline-flex"}
      w={fullWidth ? "full" : undefined}
      spacing="2xs"
      p="2xs"
      bg="segmented.bg"
      borderRadius={rounded ? "full" : "component.lg"}
    >
      {/* the sliding thumb — options render above it */}
      {showThumb && (
        <Box
          aria-hidden
          position="absolute"
          top="2xs"
          bottom="2xs"
          left="0"
          borderRadius={rounded ? "full" : "component.md"}
          bg="bg.component"
          boxShadow="seg-thumb"
          transform={`translateX(${pos.left}px)`}
          width={`${pos.width}px`}
          transition={armed ? SLIDE : "none"}
          willChange="transform, width"
          pointerEvents="none"
          sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
        />
      )}
      {options.map((o) => {
        const selected = o.value === value;
        const button = (
          <SegmentButton
            key={o.value}
            label={o.label}
            leadingIcon={o.icon}
            iconOnly={o.iconOnly}
            count={o.count}
            countTone={o.countTone}
            isSelected={selected}
            size={size}
            rounded={rounded}
            fullWidth={fullWidth}
            // the control owns the thumb (a single sliding surface), so the
            // options don't draw their own.
            showThumb={false}
            // forceState pins the visual state of the SELECTED option only;
            // other options stay live so real :hover applies to them.
            forceState={selected ? forceState : "default"}
            onClick={() => onChange?.(o.value)}
          />
        );
        if (!o.tooltip) return button;
        // span host: Tooltip needs a ref-forwarding child. The thumb measure
        // queries buttons (not children), so the wrapper doesn't disturb it.
        return (
          <Tooltip key={o.value} label={o.tooltip} placement="top" hasArrow>
            <Box as="span" display="inline-flex" flex={fullWidth ? 1 : undefined}>
              {button}
            </Box>
          </Tooltip>
        );
      })}
    </HStack>
  );
}

export default SegmentedControl;
